import React, { FC, useState, useCallback } from "react";
import Modal, { BaseModalProps } from "components/Modal/Modal";
import { t, Trans } from "@lingui/macro";
import RequestBrochureModalForm from "./RequestBrochureModalForm";
import SubscribeFormInfoCard from "components/SubscribeFormInfoCard/SubscribeFormInfoCard";
import { BrochureFragment } from "types/graphql-types";
import { isSSR } from "../../utils/ssr";
import ContentWithSideCard from "components/ContentWithSideCard/ContentWithSideCard";
import { ModalBodyTitle, ModalBody } from "@silversea-ssc/ui-components-react";

interface RequestBrochureModalProps {
  isOpen: boolean;
  onAfterOpen?: () => void;
  onRequestClose: () => void;
  brochure: BrochureFragment;
  thankYouPageURL?: string;
}

const RequestBrochureModal: FC<RequestBrochureModalProps> = ({
  isOpen,
  onAfterOpen,
  onRequestClose,
  brochure,
  thankYouPageURL
}) => {
  const title = t`Free Brochure`;
  const redirectTo = thankYouPageURL
    ? `${thankYouPageURL}?brochure=${brochure.brochureCode}`
    : undefined;

  const [isSubmitted, setIsSubmitted] = useState(false);

  const resetOnAfterOpen = useCallback(() => {
    setIsSubmitted(false);
    if (onAfterOpen) onAfterOpen();
  }, [onAfterOpen]);

  const markFormAsSubmitted = useCallback(() => {
    if (!isSubmitted) {
      //navigate(redirectTo, { replace: true });
      if (!isSSR() && redirectTo) {
        window.location.href = redirectTo;
      }
      setIsSubmitted(true);
    }
  }, [isSubmitted, redirectTo]);

  const modalProps: BaseModalProps = {
    isOpen: isOpen,
    onAfterOpen: resetOnAfterOpen,
    onRequestClose: onRequestClose,
    contentLabel: title
  };

  return (
    <div>
      <Modal title={title} modal={modalProps} size="medium">
        {isSubmitted ? (
          <React.Fragment>
            <div style={{ textAlign: "center" }}>
              <a href={redirectTo}>Redirecting...</a>
            </div>
          </React.Fragment>
        ) : (
          <Form brochure={brochure} setIsSubmitted={markFormAsSubmitted} />
        )}
      </Modal>
    </div>
  );
};

const Form: React.FC<{
  brochure: BrochureFragment;
  setIsSubmitted: (value: boolean) => void;
}> = ({ brochure, setIsSubmitted }) => {
  const header = (
    <React.Fragment>
      <ModalBodyTitle>
        <Trans>VIEW ONLINE OUR FREE BROCHURE</Trans>
      </ModalBodyTitle>
      <div>
        <Trans>Fill in the form to receive our free brochure via email</Trans>
      </div>
    </React.Fragment>
  );

  const form = (
    <RequestBrochureModalForm
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      brochureCode={brochure.brochureCode!}
      onSubmitSuccess={() => setIsSubmitted(true)}
    />
  );

  const card = (
    <SubscribeFormInfoCard
      image={brochure.thumbnail}
      title={brochure.title || ""}
    />
  );

  return (
    <ModalBody variant={card ? "wide" : "boxed"}>
      <ContentWithSideCard header={header} card={card}>
        {form}
      </ContentWithSideCard>
    </ModalBody>
  );
};

export default RequestBrochureModal;
